<template>
  <div v-if="reviews.length">
    <horizontal-divider />
    <!-- Review -->
    <div v-for="(review, index) in reviews" :key="index" class="review-item mt-16">
      <div class="flex justify-between items-center">
        <p class="text-strong-body text-grey-header">{{ review.node.reviewerShortName }}</p>
        <star-rating :showCount="false" :count="0" :average="convertStarRating(review.node.stars)" :starSize="16" class="!py-0" />
      </div>
      <p class="text-small text-grey-body mt-4">{{ formatDateToMonthNameYear(review.node.at) }}</p>
      <p class="text-body text-grey-body mt-4">{{ review.node.publicFeedback }}</p>

      <div class="flex flex-col">
        <!-- Review response (owner view) -->
        <template v-if="showOwnerView">
          <div v-if="review.node.response" class="ml-24 mt-8">
            <p class="text-s font-medium text-grey-header">{{ reviewResponseTitle }}</p>
            <p class="text-small text-grey-body mt-4">{{ review.node.response }}</p>
          </div>
          <poppins-button
            :value="review.node.response ? 'Edit' : 'Reply'"
            variant="text"
            class="ml-auto mt-4"
            :href="`/provider/reviews/reply?review_id=${review.node.id}&returnPath=${returnPath}`"
          />
        </template>

        <!-- Review response (non-owner view) -->
        <template v-else-if="showReplies && review.node.response">
          <button
            v-if="!expandedResponses[index]"
            class="ml-auto text-s font-medium text-grey-clickable underline mt-8"
            @click="toggleResponse(index)"
          >
            {{ `${revieweeDisplayName} replied` }}
          </button>
          <div v-else class="ml-24 mt-8">
            <p class="text-s font-medium text-grey-header">{{ reviewResponseTitle }}</p>
            <p class="text-small text-grey-body mt-4">{{ review.node.response }}</p>
            <button class="text-small text-grey-clickable underline mt-4" @click="toggleResponse(index)">View less</button>
          </div>
        </template>
      </div>

      <horizontal-divider class="mt-16" />
    </div>

    <poppins-button
      v-if="hasMoreReviews"
      :loading="viewMoreLoading"
      value="View More"
      variant="outlined"
      size="small"
      class="mt-16"
      data-test-id="view-more-btn"
      :class="fullWidthBtn ? 'w-full' : ''"
      @click.prevent="handleViewMore"
    />
  </div>
</template>

<script lang="ts">
import HorizontalDivider from '@/components/divider/HorizontalDivider.vue';
import StarRating from '@/components/star_rating/StarRating.vue';
import PoppinsButton from '@/components/button/Button.vue';

interface Review {
  node: {
    id: string;
    reviewerShortName: string;
    stars: string;
    at: string;
    publicFeedback: string;
    response: string;
  };
}

export default {
  name: 'ReviewsList',
  components: {
    HorizontalDivider,
    StarRating,
    PoppinsButton,
  },
  props: {
    reviews: {
      type: Array as () => Array<Review>,
      required: true,
    },
    totalReviewCount: {
      type: Number,
      required: true,
    },
    showReplies: {
      type: Boolean,
      default: false,
    },
    revieweeDisplayName: {
      type: String,
      default: '',
    },
    showOwnerView: {
      type: Boolean,
      default: false,
    },
    fullWidthBtn: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      viewMoreLoading: false,
      expandedResponses: {},
    };
  },
  computed: {
    hasMoreReviews(): boolean {
      return this.reviews.length < this.totalReviewCount;
    },
    reviewResponseTitle(): string {
      return this.showOwnerView ? 'Your response' : `${this.revieweeDisplayName}'s response`;
    },
    returnPath(): string {
      return encodeURIComponent(window.location.pathname);
    },
  },
  watch: {
    reviews(): void {
      this.viewMoreLoading = false;
    },
  },
  methods: {
    convertStarRating(rating: string): number {
      const ratingMap: Record<string, number> = {
        ONE_STAR: 1,
        TWO_STARS: 2,
        THREE_STARS: 3,
        FOUR_STARS: 4,
        FIVE_STARS: 5,
      };
      return ratingMap[rating] || 0;
    },
    formatDateToMonthNameYear(dateString: string): string {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    },
    handleViewMore(): void {
      this.viewMoreLoading = true;
      this.$emit('view-more-reviews');
    },
    toggleResponse(index) {
      this.$set(this.expandedResponses, index, !this.expandedResponses[index]);
    },
  },
};
</script>
